import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { Typography, Avatar } from '@mui/material';
import socketClient from '../../socket';
import GetValidatedTokenData from '../../utils/helper';
import { formatMessageTime } from '../../utils/formatMessageTime';
import { Stack } from '@mui/system';
import { useChatStore } from '../../Redux/Slices/chatSlice';

/**
 * Listens for incoming messages globally (at the org level).
 * Shows a toast notification when the user receives a message
 * and is NOT currently on the chat page.
 */
export default function GlobalMessageNotifier() {
  const { openChatInfo } = useChatStore('openChatInfo');
  const navigate = useNavigate();
  const { orgName } = useParams();

  function handleIncomingMessage(data) {
    const currentUser = GetValidatedTokenData();
    const msg = Array.isArray(data) ? data[0] : data;

    if (!msg || msg.sender_id === currentUser?.id) return;
    if (openChatInfo?.chatData?.id === msg?.chat_id) return;
    const chatId = msg.group_id || msg.chat_id;
    const isGroup = Boolean(msg.chatType === 'GROUP');
    const targetTab = isGroup ? 'GROUP' : 'USER';
    const senderName =
      msg.senderData?.firstName && msg.senderData?.lastName
        ? `${msg.senderData.firstName} ${msg.senderData.lastName}`
        : msg.senderData?.firstName || msg.senderData?.lastName || msg.displayName || 'Someone';
    const chatName = msg.displayName || 'Group';
    const preview =
      msg.message?.length > 100
        ? msg.message.substring(0, 100) + '...'
        : msg.message || 'sent a message';
    const avatarUrl = isGroup
      ? ''
      : msg.senderData?.profilePhotoLink ||
        msg.senderData?.profilePic ||
        msg.senderData?.avatar ||
        '';
    const avatarText = isGroup ? chatName?.charAt(0) : senderName?.charAt(0);
    const time = formatMessageTime(msg.created_at || Date.now());

    toast(
      <CustomToast
        senderName={senderName}
        chatName={chatName}
        avatarText={avatarText}
        isGroup={isGroup}
        preview={preview}
        avatarUrl={avatarUrl}
        time={time}
      />,
      {
        position: 'top-right',
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        theme: localStorage.getItem('theme') === 'dark' ? 'dark' : 'light',
        style: {
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          cursor: 'pointer',
        },
        onClick: () => {
          if (!orgName || !chatId) return;
          navigate(`/org/${orgName}/chat?tab=${targetTab}&chat-id=${chatId}`);
        },
      },
    );
  }

  useEffect(() => {
    socketClient.on('message', handleIncomingMessage);
    return () => {
      socketClient.off('message', handleIncomingMessage);
    };
  }, [openChatInfo]);
  return null;
}

function CustomToast({ senderName, preview, avatarUrl, avatarText, time, chatName, isGroup }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
      <Avatar
        src={avatarUrl}
        sx={{
          width: 42,
          height: 42,
          bgcolor: 'var(--color-primary)',
          fontSize: '18px',
        }}
      >
        {avatarText?.toUpperCase()}
      </Avatar>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '70%',
            }}
          >
            {isGroup ? chatName : senderName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.72rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {time}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: '0.82rem',
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isGroup ? `${senderName}: ${preview}` : preview}
        </Typography>
      </Stack>
    </Stack>
  );
}
